import { SolNavbar, SolNavbarItem } from '@sol/components';
import { SolIcon } from '@sol/icons';
import { GeneratedCatalogDocument, generatedCatalogDocuments } from './generated/catalog-documents.generated';
import { SolCatalogRouteId } from './models/sol-catalog-route-id';

const overviewDocuments = generatedCatalogDocuments.filter(document => document.group === 'overview');
const generalDocuments = generatedCatalogDocuments.filter(document => document.group === 'general');
const componentDocuments = generatedCatalogDocuments.filter(document => document.group === 'components');
const aiDirectiveDocuments = generatedCatalogDocuments.filter(document => document.group === 'ai-directives');

function toDocumentItem(document: GeneratedCatalogDocument): SolNavbarItem {
  return SolNavbarItem.of(document.routePath, {
    displayName: document.isIndex ? 'Overview' : document.title,
  });
}

function toGroupItem(id: string, displayName: string, documents: readonly GeneratedCatalogDocument[]): SolNavbarItem {
  return SolNavbarItem.of(id, {
    displayName,
    children: documents.map(document => toDocumentItem(document)),
  });
}

export const navbar: SolNavbar = {
  menu: [
    SolNavbarItem.of(SolCatalogRouteId.documents_sol_catalog, {
      displayName: 'Getting started',
      icon: SolIcon.info,
    }),
    SolNavbarItem.of(SolCatalogRouteId.documents_ux_foundation, {
      displayName: 'UX foundation',
      icon: SolIcon.document,
      children: [
        ...overviewDocuments.map(document => toDocumentItem(document)),
        toGroupItem('documents-ux-foundation-general', 'General', generalDocuments),
        toGroupItem('documents-ux-foundation-components', 'Components', componentDocuments),
        toGroupItem('documents-ux-foundation-ai-directives', 'AI directives', aiDirectiveDocuments),
      ],
    }),
    SolNavbarItem.of(SolCatalogRouteId.components, {
      displayName: 'Angular',
      children: [
        SolNavbarItem.of(SolCatalogRouteId.components_overview, {
          displayName: 'Sól library',
          icon: SolIcon.application,
        }),
        SolNavbarItem.of(SolCatalogRouteId.components_core, {
          displayName: 'Components',
          icon: SolIcon.environment,
          children: [
            SolNavbarItem.of(SolCatalogRouteId.components_core_accordion, {
              displayName: 'Accordion',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_alert, {
              displayName: 'Alert',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_avatar, {
              displayName: 'Avatar',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_badge, {
              displayName: 'Badge',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_banner, {
              displayName: 'Banner',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_big_checkbox, {
              displayName: 'Big checkbox',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_big_radiobutton, {
              displayName: 'Big radiobutton',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_budget, {
              displayName: 'Budget',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_button, {
              displayName: 'Button',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_button_group, {
              displayName: 'Button group',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_card, {
              displayName: 'Card',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_chart, {
              displayName: 'Chart',
              icon: SolIcon.statistics,
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_checkbox, {
              displayName: 'Checkbox',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_comment, {
              displayName: 'Comment',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_date_field, {
              displayName: 'Date field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_diff, {
              displayName: 'Diff',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_empty_state, {
              displayName: 'Empty state',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_file_upload, {
              displayName: 'File upload',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_filter_field, {
              displayName: 'Filter field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_icon, {
              displayName: 'Icon',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_image, {
              displayName: 'Image',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_layout, {
              displayName: 'Layout',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_list, {
              displayName: 'List',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_loader, {
              displayName: 'Loader',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_log, {
              displayName: 'Log',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_markdown, {
              displayName: 'Markdown',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_query_field, {
              displayName: 'Query field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_radio_button, {
              displayName: 'Radiobutton',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_relative_date_field, {
              displayName: 'Relative date field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_scroll_view, {
              displayName: 'Scroll view',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_select, {
              displayName: 'Select',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_severity, {
              displayName: 'Severity',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_snippet, {
              displayName: 'Snippet',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_spinner, {
              displayName: 'Spinner',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_steps, {
              displayName: 'Steps',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_switch, {
              displayName: 'Switch',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_table, {
              displayName: 'Table',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_tag, {
              displayName: 'Tag',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_tag_color, {
              displayName: 'Tag color',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_text_box, {
              displayName: 'Text box',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_text_area, {
              displayName: 'Text area',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_text_field, {
              displayName: 'Text field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_time_field, {
              displayName: 'Time field',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_toggle_chip, {
              displayName: 'Toggle chip',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_tooltip, {
              displayName: 'Tooltip',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_trend, {
              displayName: 'Trend',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_core_view_box, {
              displayName: 'View box',
            }),
          ],
        }),
        SolNavbarItem.of(SolCatalogRouteId.components_services, {
          displayName: 'Services',
          icon: SolIcon.asm_api,
        }),
        SolNavbarItem.of(SolCatalogRouteId.components_tools, {
          displayName: 'Tools',
          icon: SolIcon.wrench,
          children: [
            SolNavbarItem.of(SolCatalogRouteId.components_tools_workbench, {
              displayName: 'Workbench',
            }),
            SolNavbarItem.of(SolCatalogRouteId.components_tools_variant, {
              displayName: 'Variant',
            }),
          ],
        }),
      ],
    }),
  ],
  system: [],
  account: [
    SolNavbarItem.of(SolCatalogRouteId.account_section1, {
      children: [
        SolNavbarItem.of(SolCatalogRouteId.account_preferences, {
          displayName: 'Preferences',
        }),
      ],
    }),
    SolNavbarItem.of(SolCatalogRouteId.account_section2, {
      children: [
        SolNavbarItem.of(SolCatalogRouteId.account_logout, {
          displayName: 'Logout',
        }),
      ],
    }),
  ],
};
